import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "react-hot-toast";

import LoginPage           from "./pages/auth/LoginPage";
import CompleteProfile     from "./pages/auth/CompleteProfile";
import Dashboard           from "./pages/dashboard/Dashboard";
import Marketplace         from "./pages/marketplace/Marketplace";
import ProductDetail       from "./pages/product/ProductDetail";
import SellProduct         from "./pages/dashboard/SellProduct";
import EditProduct         from "./pages/dashboard/EditProduct";
import ProfilePage         from "./pages/profile/ProfilePage";
import EditProfile         from "./pages/profile/EditProfile";
import CartPage            from "./pages/cart/CartPage";
import AdminLayout         from "./pages/admin/AdminLayout";
import AdminDashboard      from "./pages/admin/AdminDashboard";
import AdminUsers          from "./pages/admin/AdminUsers";
import AdminUserDetail     from "./pages/admin/AdminUserDetail";
import AdminProducts       from "./pages/admin/AdminProducts";
import AdminProductDetail  from "./pages/admin/AdminProductDetail";
import AdminNotifications  from "./pages/admin/AdminNotifications";
import AppLayout           from "./components/layout/AppLayout";
import LostFound           from "./pages/lostfound/LostFound";
import AdminLostFound      from "./pages/admin/AdminLostFound";
import { SocketProvider }  from "./context/SocketContext";
import ChatPage            from "./pages/chat/ChatPage";
import AdminChat           from "./pages/admin/AdminChat";
import AdminReports        from "./pages/admin/AdminReports";
import AboutPage           from "./pages/about/AboutPage";
import InstallPage         from "./pages/about/InstallPage";
import PublicLayout        from "./components/layout/PublicLayout";
import HomePage            from "./pages/home/HomePage";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const ProfileGuard = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.profileComplete) return <Navigate to="/complete-profile" replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading, isAdmin } = useAuth();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/app/dashboard" replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (user) return <Navigate to="/app/dashboard" replace />;
  return children;
};

const FullPageLoader = () => (
  <div style={{
    height: "100vh", display: "flex",
    alignItems: "center", justifyContent: "center",
    background: "var(--bg)",
  }}>
    <div className="spinner spinner-lg" />
  </div>
);

export default function App() {
  return (
    <AuthProvider>
      <SocketProvider>
        <BrowserRouter>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 3500,
              style: {
                fontFamily: "var(--font-body)",
                fontSize: "0.9rem",
                borderRadius: "var(--radius)",
                border: "1px solid var(--border)",
                boxShadow: "var(--shadow-md)",
                color: "var(--text)",
                background: "var(--surface)",
              },
              success: { iconTheme: { primary: "var(--success)", secondary: "#fff" } },
              error:   { iconTheme: { primary: "var(--danger)",  secondary: "#fff" } },
            }}
          />
          <Routes>

            {/* ── Fully public pages (no login required) ── */}
            <Route path="/" element={<PublicLayout />}>
              <Route index element={<PublicRoute><HomePage /></PublicRoute>} />
              <Route path="explore"  element={<Marketplace public />} />
              <Route path="item/:id" element={<ProductDetail public />} />
              <Route path="about"    element={<AboutPage />} />
              <Route path="install"  element={<InstallPage />} />
            </Route>

            {/* ── Auth pages (redirect to dashboard if already logged in) ── */}
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/complete-profile"
              element={<ProtectedRoute><CompleteProfile /></ProtectedRoute>}
            />

            {/* ── App with sidebar (requires login + complete profile) ── */}
            <Route path="/app" element={<ProfileGuard><AppLayout /></ProfileGuard>}>
              <Route index                       element={<Navigate to="/app/dashboard" replace />} />
              <Route path="dashboard"            element={<Dashboard />} />
              <Route path="sell"                 element={<SellProduct />} />
              <Route path="edit-product/:id"     element={<EditProduct />} />
              <Route path="marketplace"          element={<Marketplace />} />
              <Route path="product/:id"          element={<ProductDetail />} />
              <Route path="profile"              element={<ProfilePage />} />
              <Route path="profile/edit"         element={<EditProfile />} />
              <Route path="profile/:id"          element={<ProfilePage />} />
              <Route path="user/:id"             element={<ProfilePage />} />
              <Route path="cart"                 element={<CartPage />} />
              <Route path="lostfound"            element={<LostFound />} />
              <Route path="chat"                 element={<ChatPage />} />
              <Route path="chat/:conversationId" element={<ChatPage />} />
              <Route path="about"                element={<AboutPage />} />
              <Route path="install"              element={<InstallPage />} />
            </Route>

            {/* ── Admin ── */}
            <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
              <Route index                element={<AdminDashboard />} />
              <Route path="users"         element={<AdminUsers />} />
              <Route path="users/:id"     element={<AdminUserDetail />} />
              <Route path="products"      element={<AdminProducts />} />
              <Route path="products/:id"  element={<AdminProductDetail />} />
              <Route path="lostfound"     element={<AdminLostFound />} />
              <Route path="chat"          element={<AdminChat />} />
              <Route path="reports"       element={<AdminReports />} />
              <Route path="notifications" element={<AdminNotifications />} />
            </Route>

            {/* ── Fallback ── */}
            <Route path="*" element={<Navigate to="/" replace />} />

          </Routes>
        </BrowserRouter>
      </SocketProvider>
    </AuthProvider>
  );
}